import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  signal,
} from '@angular/core';
import { ChecklistenService } from '../../services/checklisten.service';
import { FormatPipe, IconComponent, TranslatePipe } from '@softline/ui-core';
import { ActivatedRoute, RouterLink } from '@angular/router';
import moment from 'moment/moment';
import { base64Encode, DateTimeService } from '@softline/core';
import { DonutChartComponent, ResourceContainerComponent } from '@softapps/core';
import { DatePipe, NgClass, SlicePipe } from '@angular/common';

@Component({
  selector: 'soft-checklist-widget',
  templateUrl: './checklist-widget.component.html',
  styleUrls: ['./checklist-widget.component.css'],
  imports: [
    FormatPipe,
    IconComponent,
    RouterLink,
    TranslatePipe,
    ResourceContainerComponent,
    SlicePipe,
    NgClass,
    DonutChartComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    class: 'flex flex-col soft-card rounded',
  },
  providers: [DatePipe],
})
export class ChecklistWidgetComponent {
  readonly #service = inject(ChecklistenService);
  readonly dateService = inject(DateTimeService);
  readonly activatedRoute = inject(ActivatedRoute);

  readonly date = signal<string | undefined>(moment().toISOString());

  readonly gaugeMaxWidth = input<string>('max-w-[228px]');

  readonly zeitraum = computed(() => {
    const date = this.date();

    return {
      from: moment(date).startOf('isoWeek').toISOString(),
      to: moment(date).endOf('isoWeek').toISOString(),
    };
  });

  readonly params = computed(() => {
    return {
      ...this.zeitraum(),
      all: true,
    };
  });

  readonly queryString = computed(() =>
    base64Encode(JSON.stringify(this.#service.params()))
  );

  readonly currentUrl = computed(() =>
    base64Encode(this.activatedRoute.snapshot.routeConfig?.path ?? '/')
  );

  readonly checklistResource = this.#service.resource;

  readonly checklistData = computed(() => {
    const data = this.#service.checklisten() ?? [];
    const dataOnlyOpen = data.filter((o) => o.status === 'B');
    return {
      countAll: data.length,
      countOnlyOpen: dataOnlyOpen.length,
      data: dataOnlyOpen,
    };
  });

  constructor() {
    this.#service.trackParams(this.params);
  }

  onDateChange(date: string, days = 0): void {
    const m = moment(date).add(days, 'week').toISOString();
    if (this.date() === m) return;
    this.date.set(m);
  }

  protected readonly base64Encode = base64Encode;
  protected readonly JSON = JSON;
}
